























import Vue, { PropType } from "vue";
import { CompanyDetailReviewCategory } from "@/api/types";
import { Data, Methods, Computed, Props } from "./types";
import {
  ReviewRatingSeminar,
  ReviewRatingIntern,
  ReviewRatingRecruitment,
  Genre
} from "@/components/Pages/CompanyDetail/types";
import Heading from "@/components/Atoms/Typography/Heading/index.vue";
import RadarChart from "../../Molecules/RadarChart/index.vue";
import ChartCard from "../../Atoms/ChartCard/index.vue";
import ChartLegend from "../../Atoms/ChartLegend/index.vue";
import { getGraduateYearColorInCompany } from "../../../../../helpers/company-detail-helper";
import { line10, grayscale10, grayscale60 } from "@/assets/styles/vars";
import { INIT_REVIEW_RADAR_CART } from "@/constants/company";
import { isNotNullOrUndefined } from "@/helpers/type-helper";
import { getInitialSelectedChartYears } from "@/helpers/date-helper";

// NOTE 半角文字は1文字、全角文字は2文字として文字数をカウント
const getLength = (str: string) => {
  var result = 0;
  [...Array(str.length)].forEach((_, i) => {
    var chr = str.charCodeAt(i);
    if (
      (chr >= 0x00 && chr < 0x81) ||
      chr === 0xf8f0 ||
      (chr >= 0xff61 && chr < 0xffa0) ||
      (chr >= 0xf8f1 && chr < 0xf8f4)
    ) {
      //半角文字の場合は1を加算
      result += 1;
    } else {
      //それ以外の文字の場合は2を加算
      result += 2;
    }
  });
  return result;
};

const getLatestYear = (reviewGraphs: Props["reviewGraphs"]) => {
  const reviewGraphYears = reviewGraphs.map(e => e.graduate_year);
  return Math.max(...reviewGraphYears);
};

const getInitialSelectedYears = (reviewGraphs: Props["reviewGraphs"]) => {
  const latestYear = getLatestYear(reviewGraphs);
  return getInitialSelectedChartYears(latestYear);
};

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "ReviewRadarChart",
  components: {
    Heading,
    RadarChart,
    ChartCard,
    ChartLegend
  },
  props: {
    reviewGraphs: {
      type: Array as PropType<Props["reviewGraphs"]>,
      required: true
    },
    review: {
      type: Array as PropType<Props["review"]>,
      required: true
    }
  },
  data() {
    return {
      selectedYears: getInitialSelectedYears(this.reviewGraphs)
    };
  },
  computed: {
    latestYear() {
      return getLatestYear(this.reviewGraphs);
    },
    chartConfigs() {
      return INIT_REVIEW_RADAR_CART.map(({ category }, _, categoryArray) => {
        const reviewsGraphByCategory = this.reviewGraphs
          .filter(reviewGraph => reviewGraph.category === category)
          .sort((a, b) => b.graduate_year - a.graduate_year);

        if (!reviewsGraphByCategory) return;

        const selectedReviewCount = reviewsGraphByCategory.reduce(
          (total, currentReview) => {
            const isSelectedReview = this.selectedYears.some(
              selectedYear => selectedYear === currentReview.graduate_year
            );
            if (isSelectedReview) {
              return total + currentReview.review_count;
            }
            return total;
          },
          0
        );

        const targetReview = this.review.find(
          review => review.category === category
        );
        if (!targetReview) return;

        const chartConfig: Computed["chartConfigs"][number] = {
          category,
          config: {
            type: "radar",
            options: {
              title: {
                text: `${targetReview.name}の評価（${selectedReviewCount}件）`
              },
              maintainAspectRatio: false,
              legend: {
                display: false
              },
              tooltips: {
                mode: "single",
                titleFontColor: grayscale60,
                titleMarginBottom: 4.8,
                titleFontStyle: "400",
                bodyFontStyle: "700",
                callbacks: {
                  title: () => "",
                  labelTextColor: tooltipItems => {
                    const graduateYear =
                      reviewsGraphByCategory[tooltipItems.datasetIndex!]
                        .graduate_year;
                    return getGraduateYearColorInCompany(
                      this.latestYear,
                      graduateYear
                    ).color;
                  },
                  label: tooltipItems => {
                    const reviewRating = String(
                      Number(tooltipItems.value).toFixed(1)
                    );
                    this.onHoverReviewRadarChart(category);
                    return reviewRating;
                  }
                },
                position: "nearest",
                backgroundColor: grayscale10,
                displayColors: false,
                borderColor: line10,
                borderWidth: 1
              },
              scale: {
                pointLabels: {
                  callback: label => {
                    return `${label}`;
                  },
                  fontColor: grayscale60,
                  fontSize: 10,
                  fontStyle: "500"
                },
                ticks: {
                  display: false,
                  stepSize: 1,
                  suggestedMin: 0,
                  suggestedMax: 5
                }
              }
            },
            data: {
              labels: (Object.keys(
                this.getChartLabelTypes(category)
              ) as Genre[]).map(key => {
                const numberOfMaxLineLength = 8;
                const getlabel = (str: string): string => {
                  if (str.length > numberOfMaxLineLength) {
                    return str.slice(0, 5) + "\n" + str.slice(5);
                  }
                  return str;
                };

                // NOTE: 1行の最大長の文字(改行が起きた時は、最も長い行を返す)
                const getLineMaxLength = (str: string): string => {
                  if (str.length > numberOfMaxLineLength) {
                    return getLength(str.slice(0, 5)) > getLength(str.slice(5))
                      ? str.slice(0, 5)
                      : str.slice(5);
                  }
                  return str;
                };

                let label = getlabel(this.getChartLabelTypes(category)[key]);
                const maxLengthLineLabel = getLineMaxLength(
                  this.getChartLabelTypes(category)[key]
                );

                let maxLength = 0;
                categoryArray.forEach(({ category: c }) => {
                  const labelTypes = getLineMaxLength(
                    this.getChartLabelTypes(c)[key]
                  );
                  const length = getLength(labelTypes);
                  if (maxLength < length) {
                    maxLength = length;
                  }
                });

                const differenceLength =
                  maxLength - getLength(maxLengthLineLabel);

                // NOTE [暫定対応]
                // レーダーチャートのラベルの文字数でチャートサイズが変わってしまうため、半角空文字で長さを揃えています。
                [...Array(differenceLength)].forEach((_, i) => {
                  label = i % 2 === 0 ? label + " " : " " + label;
                });

                return label;
              }),
              datasets: reviewsGraphByCategory.map(review => {
                const hiddenReview = !this.selectedYears.some(
                  selectedYear => selectedYear === review.graduate_year
                );
                return {
                  hidden: hiddenReview,
                  label: String(review.graduate_year),
                  data: [
                    review.rating1,
                    review.rating2,
                    review.rating3,
                    review.rating4,
                    review.rating5,
                    review.rating6
                  ],
                  pointRadius: 1.76,
                  borderWidth: 1,
                  borderColor: getGraduateYearColorInCompany(
                    this.latestYear,
                    review.graduate_year
                  ).color,
                  backgroundColor: getGraduateYearColorInCompany(
                    this.latestYear,
                    review.graduate_year
                  ).paleColor
                };
              })
            }
          }
        };
        return chartConfig;
      }).filter(isNotNullOrUndefined);
    }
  },
  methods: {
    getChartLabelTypes(category) {
      switch (category) {
        case CompanyDetailReviewCategory.SEMINAR: {
          const { rating_ave, ...genre } = ReviewRatingSeminar;
          return genre;
        }
        case CompanyDetailReviewCategory.INTERN: {
          const { rating_ave, ...genre } = ReviewRatingIntern;
          return genre;
        }
        case CompanyDetailReviewCategory.RECRUITMENT: {
          const { rating_ave, ...genre } = ReviewRatingRecruitment;
          return genre;
        }
        // TODO: ENTRY_SHEET の項目は今後対応
        // case CompanyDetailReviewCategory.ENTRY_SHEET: {
        //   const { rating_ave, ...genre } = ReviewRatingRecruitment;
        //   return genre;
        // }
        default:
          throw "";
      }
    },
    onHoverReviewRadarChart(category) {
      switch (category) {
        case CompanyDetailReviewCategory.SEMINAR: {
          // NOTE: SEMINARのチャートを実装する際に、GAも実装する。
          return;
        }
        case CompanyDetailReviewCategory.INTERN: {
          return;
        }
        case CompanyDetailReviewCategory.RECRUITMENT: {
          return;
        }
        // TODO: ENTRY_SHEET の項目は今後対応
        // case CompanyDetailReviewCategory.ENTRY_SHEET: {
        // gaEventRecruitmentReviewRadarChartHover(this.$gtag.event, {
        //   reviewRating
        // });
        // return;
        // }
        default:
          throw "";
      }
    }
  }
});
